/**
 * Dark Theme Configuration
 *
 * Complete MUI dark theme with glassmorphism, glows, and modern aesthetics.
 * Inspired by Stripe's dark mode with deep navy backgrounds.
 */

import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { baseColors, darkPalette } from './palette';
import { darkShadows, glassEffects, glowEffects } from './shadows';
import { muiTransitions, transitionPresets } from './transitions';
import { typographyVariants } from './typography';

// ============================================================================
// Dark Theme Options
// ============================================================================

const darkThemeOptions: ThemeOptions = {
  palette: darkPalette,
  typography: typographyVariants,
  transitions: muiTransitions,
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    darkShadows[1],
    darkShadows[2],
    darkShadows[2],
    darkShadows[3],
    darkShadows[3],
    darkShadows[3],
    darkShadows[3],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[6],
  ],
  components: {
    // ========================================================================
    // CssBaseline
    // ========================================================================
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: 'rgba(255,255,255,0.2) transparent',
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 4,
            '&:hover': {
              background: 'rgba(255,255,255,0.3)',
            },
          },
        },
      },
    },

    // ========================================================================
    // Button
    // ========================================================================
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontWeight: 600,
          textTransform: 'none',
          transition: transitionPresets.button,
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.MuiButton-contained.MuiButton-colorPrimary': {
            // Dark-mode primary is F.mint (`baseColors.teal[400]`) — see palette.ts.
            background: `linear-gradient(135deg, ${baseColors.teal[400]} 0%, ${baseColors.teal[500]} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${baseColors.teal[300]} 0%, ${baseColors.teal[400]} 100%)`,
              boxShadow: `${darkShadows[3]}, ${glowEffects.teal.strong}`,
            },
          },
          '&.MuiButton-contained.MuiButton-colorSecondary': {
            background: `linear-gradient(135deg, ${baseColors.secondary[400]} 0%, ${baseColors.secondary[500]} 100%)`,
            '&:hover': {
              boxShadow: `${darkShadows[3]}, ${glowEffects.secondary.strong}`,
            },
          },
        },
        contained: {
          boxShadow: darkShadows[2],
          '&:hover': {
            // Mint glow to match dark-mode primary (F.mint).
            boxShadow: `${darkShadows[3]}, ${glowEffects.teal.medium}`,
          },
        },
        outlined: {
          borderWidth: 1.5,
          borderColor: 'rgba(255, 255, 255, 0.2)',
          color: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            borderWidth: 1.5,
            borderColor: 'rgba(255, 255, 255, 0.4)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
        text: {
          color: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },

    // ========================================================================
    // Card (Glassmorphism)
    // ========================================================================
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: glassEffects.dark.background,
          backdropFilter: glassEffects.dark.backdropBlur,
          border: `1px solid ${glassEffects.dark.border}`,
          boxShadow: glassEffects.dark.shadow,
          transition: transitionPresets.card,
          overflow: 'visible',
          '&:hover': {
            background: glassEffects.dark.backgroundHover,
            border: `1px solid ${glassEffects.dark.borderHover}`,
            boxShadow: `${glassEffects.dark.shadowHover}, ${glowEffects.mixed.subtle}`,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '& .MuiCardActionArea-focusHighlight': {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            opacity: 0,
            transition: transitionPresets.allFast,
          },
          '&:hover .MuiCardActionArea-focusHighlight': {
            opacity: 0.04,
          },
          '&.Mui-focusVisible .MuiCardActionArea-focusHighlight': {
            opacity: 0.08,
          },
        },
      },
    },

    // ========================================================================
    // Paper (Glassmorphism)
    // ========================================================================
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          backgroundColor: darkPalette.background.paper,
        },
        elevation1: {
          boxShadow: darkShadows[1],
        },
        elevation2: {
          boxShadow: darkShadows[2],
        },
        elevation3: {
          boxShadow: darkShadows[3],
        },
        elevation4: {
          boxShadow: darkShadows[4],
        },
      },
    },

    // ========================================================================
    // TextField / Input
    // ========================================================================
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: 'transparent',
          transition: transitionPresets.input,
          '&:not(.MuiInputBase-multiline):not(.MuiInputBase-sizeSmall)': {
            height: 46,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.25)',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1.5,
            borderColor: baseColors.teal[400],
            // Mint focus ring to match dark-mode primary (F.mint).
            boxShadow: `0 0 0 3px rgba(78, 217, 164, 0.25)`,
          },
        },
        notchedOutline: {
          borderColor: 'rgba(255, 255, 255, 0.12)',
          transition: transitionPresets.input,
        },
        input: {
          backgroundColor: 'transparent',
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.4)',
            opacity: 1,
          },
          // Override browser autofill styling
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px transparent inset !important',
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.9) !important',
            caretColor: 'rgba(255, 255, 255, 0.9)',
            transition: 'background-color 5000s ease-in-out 0s',
          },
          '&:-webkit-autofill:hover, &:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px transparent inset !important',
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.9) !important',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.6)',
          '&.Mui-focused': {
            color: baseColors.teal[400],
          },
        },
      },
    },

    // ========================================================================
    // Chip
    // ========================================================================
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            boxShadow: glowEffects.primary.subtle,
          },
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.2)',
        },
      },
    },

    // ========================================================================
    // Dialog / Modal (Glassmorphism)
    // ========================================================================
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          // F.panel (#0e141b) with high opacity over the F.bg canvas.
          background: 'rgba(14, 20, 27, 0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: `${darkShadows[5]}, ${glowEffects.mixed.medium}`,
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
          padding: '24px 24px 16px',
          color: 'rgba(255, 255, 255, 0.95)',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
          gap: 12,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          },
        },
      },
    },

    // ========================================================================
    // Menu / Dropdown
    // ========================================================================
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          // F.panel (#0e141b) with high opacity — matches dialog surface.
          background: 'rgba(14, 20, 27, 0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: darkShadows[4],
          border: '1px solid rgba(255, 255, 255, 0.1)',
          marginTop: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          padding: '12px 16px',
          minHeight: 44,
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-selected': {
            // Mint-tinted selection to match dark-mode primary (F.mint).
            backgroundColor: 'rgba(78, 217, 164, 0.20)',
            '&:hover': {
              backgroundColor: 'rgba(78, 217, 164, 0.28)',
            },
          },
        },
      },
    },

    // ========================================================================
    // Tooltip
    // ========================================================================
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: baseColors.neutral[900],
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: '0.75rem',
          fontWeight: 500,
          boxShadow: darkShadows[3],
        },
        arrow: {
          color: 'rgba(255, 255, 255, 0.95)',
        },
      },
    },

    // ========================================================================
    // Avatar
    // ========================================================================
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: darkShadows[2],
        },
      },
    },

    // ========================================================================
    // Drawer
    // ========================================================================
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: darkPalette.background.default,
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: 0,
          boxShadow: darkShadows[4],
        },
      },
    },

    // ========================================================================
    // AppBar (Glassmorphism)
    // ========================================================================
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: darkShadows[1],
          backdropFilter: 'blur(20px)',
          // F.bg (#0a0e13) with high opacity — matches the v3 canvas.
          backgroundColor: 'rgba(10, 14, 19, 0.8)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },

    // ========================================================================
    // FormControlLabel — add breathing room between control and label (switch, checkbox, radio)
    // ========================================================================
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          gap: 12,
        },
      },
    },

    // ========================================================================
    // Switch
    // ========================================================================
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        switchBase: {
          '&.Mui-checked': {
            '& + .MuiSwitch-track': {
              backgroundColor: baseColors.teal[400],
              opacity: 1,
            },
          },
        },
        track: {
          borderRadius: 22,
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
        thumb: {
          boxShadow: darkShadows[2],
        },
      },
    },

    // ========================================================================
    // Skeleton (Shimmer)
    // ========================================================================
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },

    // ========================================================================
    // Tabs
    // ========================================================================
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
          backgroundColor: baseColors.teal[400],
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 48,
          color: 'rgba(255, 255, 255, 0.6)',
          transition: transitionPresets.allFast,
          '&:hover': {
            color: 'rgba(255, 255, 255, 0.9)',
          },
          '&.Mui-selected': {
            color: baseColors.teal[400],
          },
        },
      },
    },

    // ========================================================================
    // List
    // ========================================================================
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '4px 8px',
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-selected': {
            // Mint-tinted selection to match dark-mode primary (F.mint).
            backgroundColor: 'rgba(78, 217, 164, 0.20)',
            '&:hover': {
              backgroundColor: 'rgba(78, 217, 164, 0.28)',
            },
          },
        },
      },
    },

    // ========================================================================
    // Divider
    // ========================================================================
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },

    // ========================================================================
    // Table
    // ========================================================================
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: 'rgba(255, 255, 255, 0.08)',
        },
        head: {
          fontWeight: 600,
          color: 'rgba(255, 255, 255, 0.7)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
          },
        },
      },
    },

    // ========================================================================
    // Alert
    // ========================================================================
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid',
          // Alert tints aligned to v3 dark palette: mint (success),
          // F.red (error), F.amber (warning), info (kept).
          '&.MuiAlert-standard.MuiAlert-colorSuccess': {
            backgroundColor: 'rgba(78, 217, 164, 0.14)', // F.mintDim
            borderColor: 'rgba(78, 217, 164, 0.38)', // F.mintBorder
          },
          '&.MuiAlert-standard.MuiAlert-colorError': {
            backgroundColor: 'rgba(229, 115, 115, 0.14)', // F.redDim
            borderColor: 'rgba(229, 115, 115, 0.38)',
          },
          '&.MuiAlert-standard.MuiAlert-colorWarning': {
            backgroundColor: 'rgba(232, 183, 101, 0.14)', // F.amberDim
            borderColor: 'rgba(232, 183, 101, 0.38)',
          },
          '&.MuiAlert-standard.MuiAlert-colorInfo': {
            backgroundColor: 'rgba(59, 130, 246, 0.15)',
            borderColor: 'rgba(59, 130, 246, 0.3)',
          },
        },
      },
    },
  },
};

// ============================================================================
// Create and Export Theme
// ============================================================================

export const darkTheme = createTheme(darkThemeOptions);

export default darkTheme;
